import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Search, SlidersHorizontal, Car, MapPin, Loader2, X } from "lucide-react";
import { searchVehicles, getMarketplaceMakes, getMarketplaceModels, getMarketplaceYears, MarketplaceVehicle } from "@/Calls/callMarketplace";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";

const CONDITIONS = ["", "New", "Used", "Certified"];
const PRICE_RANGES = [
    { label: "Any Price", min: "", max: "" },
    { label: "Under $15k",  min: "",      max: "15000" },
    { label: "$15k–$25k",   min: "15000", max: "25000" },
    { label: "$25k–$40k",   min: "25000", max: "40000" },
    { label: "$40k–$60k",   min: "40000", max: "60000" },
    { label: "Over $60k",   min: "60000", max: "" },
];

function displayPrice(v: MarketplaceVehicle) {
    const p = Number(v.vdprice ?? v.vrprice ?? 0);
    if (!p) return "Call for Price";
    return `$${p.toLocaleString("en-US")}`;
}

function vehiclePhoto(v: MarketplaceVehicle): string | null {
    if (v.vPhotoURLs) {
        try {
            const urls = JSON.parse(v.vPhotoURLs);
            if (Array.isArray(urls) && urls[0]) return urls[0];
        } catch { /* ignore */ }
    }
    if (v.vthubmnail_file_path) return v.vthubmnail_file_path;
    return null;
}

function VehicleCard({ vehicle, onBuyOnline }: { vehicle: MarketplaceVehicle; onBuyOnline: (v: MarketplaceVehicle) => void }) {
    const photo = vehiclePhoto(vehicle);
    const price = Number(vehicle.vdprice ?? vehicle.vrprice ?? 0);
    const msrp = Number(vehicle.vrprice ?? 0);
    const savings = msrp && price && msrp > price ? msrp - price : 0;

    return (
        <Card className="overflow-hidden flex flex-col group hover:shadow-lg transition-shadow">
            {/* Photo */}
            <div className="aspect-[16/10] bg-muted relative overflow-hidden">
                {photo ? (
                    <img src={photo} alt={`${vehicle.vyear} ${vehicle.vmake} ${vehicle.vmodel}`} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                ) : (
                    <div className="w-full h-full flex items-center justify-center">
                        <Car className="h-12 w-12 text-muted-foreground/30" />
                    </div>
                )}
                {vehicle.vcondition && (
                    <span className="absolute top-2 left-2 bg-black/70 text-white text-xs px-2 py-0.5 rounded-full">
                        {vehicle.vcondition}
                    </span>
                )}
            </div>

            {/* Info */}
            <div className="p-4 flex flex-col flex-1 space-y-2">
                <div>
                    <p className="font-semibold text-sm leading-tight">
                        {vehicle.vyear} {vehicle.vmake} {vehicle.vmodel}
                    </p>
                    {vehicle.vtrim && <p className="text-xs text-muted-foreground">{vehicle.vtrim}</p>}
                </div>

                <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-muted-foreground">
                    {vehicle.vmileage && <span>{Number(vehicle.vmileage).toLocaleString()} mi</span>}
                    {vehicle.vecolor_txt && <span>{vehicle.vecolor_txt}</span>}
                    {vehicle.vbody && <span>{vehicle.vbody}</span>}
                </div>

                {vehicle.dealer && (
                    <div className="flex items-center gap-1 text-xs text-muted-foreground">
                        <MapPin className="h-3 w-3 shrink-0" />
                        <span>{vehicle.dealer.company ?? "Dealer"}{vehicle.dealer.city ? ` · ${vehicle.dealer.city}, ${vehicle.dealer.state}` : ""}</span>
                    </div>
                )}

                <div className="flex-1" />

                <div className="pt-2 border-t">
                    <div className="flex items-end justify-between mb-3">
                        <div>
                            <p className="text-xl font-bold">{displayPrice(vehicle)}</p>
                            {savings > 0 && (
                                <p className="text-xs text-emerald-600">Save ${savings.toLocaleString("en-US")}</p>
                            )}
                        </div>
                    </div>
                    <Button
                        className="w-full bg-gradient-to-r from-indigo-500 to-violet-500 text-white text-sm"
                        onClick={() => onBuyOnline(vehicle)}
                    >
                        Buy Online
                    </Button>
                </div>
            </div>
        </Card>
    );
}

export default function CustomerMarketplacePage() {
    const navigate = useNavigate();

    const [makes, setMakes] = useState<string[]>([]);
    const [models, setModels] = useState<string[]>([]);
    const [years, setYears] = useState<string[]>([]);

    const [filters, setFilters] = useState({
        make: "", model: "", year: "", condition: "", minPrice: "", maxPrice: "",
    });
    const [showFilters, setShowFilters] = useState(false);
    const [search, setSearch] = useState("");

    const [vehicles, setVehicles] = useState<MarketplaceVehicle[]>([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(false);
    const LIMIT = 24;

    // Load filter data on mount
    useEffect(() => {
        Promise.all([getMarketplaceMakes(), getMarketplaceYears()])
            .then(([m, y]) => { setMakes(m); setYears(y); })
            .catch(() => {});
    }, []);

    // Load models when make changes
    useEffect(() => {
        if (!filters.make) { setModels([]); return; }
        getMarketplaceModels(filters.make).then(setModels).catch(() => {});
    }, [filters.make]);

    const load = useCallback(async (p: number = 1) => {
        setLoading(true);
        try {
            const result = await searchVehicles({
                make: filters.make || undefined,
                model: filters.model || undefined,
                year: filters.year || undefined,
                condition: filters.condition || undefined,
                minPrice: filters.minPrice || undefined,
                maxPrice: filters.maxPrice || undefined,
                page: p,
                limit: LIMIT,
            });
            setVehicles(p === 1 ? result.vehicles : prev => [...prev, ...result.vehicles]);
            setTotal(result.total);
            setPage(p);
        } catch { /* silent */ } finally {
            setLoading(false);
        }
    }, [filters]);

    useEffect(() => { load(1); }, [load]);

    const handleBuyOnline = (v: MarketplaceVehicle) => {
        const price = Number(v.vdprice ?? v.vrprice ?? 0);
        const params = new URLSearchParams({
            vehicleId: String(v.vid),
            dealerId: String(v.dealer?.id ?? 0),
            dealerRoom: v.dealer?.token ?? "",
            price: String(price),
            year: v.vyear ?? "",
            make: v.vmake ?? "",
            model: v.vmodel ?? "",
            trim: v.vtrim ?? "",
            vin: v.vvin ?? "",
            color: v.vecolor_txt ?? "",
            mileage: v.vmileage ?? "",
        });
        navigate(`/customer/deal-builder?${params}`);
    };

    const setFilter = (k: keyof typeof filters, val: string) => {
        setFilters(prev => ({ ...prev, [k]: val, ...(k === "make" ? { model: "" } : {}) }));
        setPage(1);
    };

    const clearFilters = () => {
        setFilters({ make: "", model: "", year: "", condition: "", minPrice: "", maxPrice: "" });
        setPage(1);
    };

    const hasFilters = Object.values(filters).some(Boolean);

    // Client-side text search on loaded vehicles
    const displayed = search.trim()
        ? vehicles.filter(v => {
            const q = search.toLowerCase();
            return (
                (v.vmake ?? "").toLowerCase().includes(q) ||
                (v.vmodel ?? "").toLowerCase().includes(q) ||
                (v.vtrim ?? "").toLowerCase().includes(q) ||
                (v.vvin ?? "").toLowerCase().includes(q)
            );
        })
        : vehicles;

    return (
        <div className="max-w-7xl mx-auto space-y-6">
            <div>
                <h1 className="text-2xl font-light uppercase tracking-wide">Browse Vehicles</h1>
                <p className="text-muted-foreground text-sm mt-1">
                    {total > 0 ? `${total.toLocaleString()} vehicles available` : "Browse inventory from dealers near you"}
                </p>
            </div>

            {/* Search + filter bar */}
            <div className="flex gap-3 flex-wrap">
                <div className="relative flex-1 min-w-[200px]">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                        className="pl-9"
                        placeholder="Search make, model, VIN…"
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                    />
                </div>
                <Button
                    variant={showFilters ? "default" : "outline"}
                    onClick={() => setShowFilters(!showFilters)}
                    className="gap-2"
                >
                    <SlidersHorizontal className="h-4 w-4" />
                    Filters
                    {hasFilters && <span className="bg-indigo-500 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center">{Object.values(filters).filter(Boolean).length}</span>}
                </Button>
                {hasFilters && (
                    <Button variant="ghost" size="sm" onClick={clearFilters} className="gap-1 text-muted-foreground">
                        <X className="h-3 w-3" />
                        Clear
                    </Button>
                )}
            </div>

            {/* Expanded filters */}
            {showFilters && (
                <Card className="p-4">
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                        <div>
                            <label className="text-xs font-medium uppercase text-muted-foreground mb-1 block">Make</label>
                            <select
                                value={filters.make}
                                onChange={e => setFilter("make", e.target.value)}
                                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                            >
                                <option value="">Any Make</option>
                                {makes.map(m => <option key={m} value={m}>{m}</option>)}
                            </select>
                        </div>
                        <div>
                            <label className="text-xs font-medium uppercase text-muted-foreground mb-1 block">Model</label>
                            <select
                                value={filters.model}
                                onChange={e => setFilter("model", e.target.value)}
                                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                                disabled={!filters.make}
                            >
                                <option value="">Any Model</option>
                                {models.map(m => <option key={m} value={m}>{m}</option>)}
                            </select>
                        </div>
                        <div>
                            <label className="text-xs font-medium uppercase text-muted-foreground mb-1 block">Year</label>
                            <select
                                value={filters.year}
                                onChange={e => setFilter("year", e.target.value)}
                                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                            >
                                <option value="">Any Year</option>
                                {years.map(y => <option key={y} value={String(y)}>{y}</option>)}
                            </select>
                        </div>
                        <div>
                            <label className="text-xs font-medium uppercase text-muted-foreground mb-1 block">Condition</label>
                            <select
                                value={filters.condition}
                                onChange={e => setFilter("condition", e.target.value)}
                                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                            >
                                {CONDITIONS.map(c => <option key={c} value={c}>{c || "Any Condition"}</option>)}
                            </select>
                        </div>
                        <div className="col-span-2">
                            <label className="text-xs font-medium uppercase text-muted-foreground mb-1 block">Price Range</label>
                            <div className="flex gap-2 flex-wrap">
                                {PRICE_RANGES.map(r => (
                                    <button
                                        key={r.label}
                                        onClick={() => {
                                            setFilter("minPrice", r.min);
                                            setFilters(prev => ({ ...prev, minPrice: r.min, maxPrice: r.max }));
                                        }}
                                        className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
                                            filters.minPrice === r.min && filters.maxPrice === r.max
                                                ? "bg-indigo-500 text-white"
                                                : "bg-muted text-muted-foreground hover:bg-muted/80"
                                        }`}
                                    >
                                        {r.label}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                </Card>
            )}

            {/* Marketplace partner badges */}
            <div className="flex flex-wrap gap-2 items-center">
                <span className="text-xs text-muted-foreground">Also on:</span>
                {[
                    { label: "BuyHerePayHere.us", url: "https://buyherepayhereus.com", color: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400" },
                    { label: "WeFinanceHere",     url: "https://wefinancehere.com",    color: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400" },
                    { label: "AutoCityMag",        url: "https://autocitymag.com",      color: "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400" },
                ].map(p => (
                    <a
                        key={p.label}
                        href={p.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`px-3 py-1 rounded-full text-xs font-medium hover:opacity-80 transition-opacity ${p.color}`}
                    >
                        {p.label}
                    </a>
                ))}
            </div>

            {/* Results */}
            {loading && vehicles.length === 0 ? (
                <div className="flex items-center justify-center py-24">
                    <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
            ) : displayed.length === 0 ? (
                <div className="text-center py-20 space-y-3">
                    <Car className="h-16 w-16 mx-auto text-muted-foreground/20" />
                    <p className="text-muted-foreground">No vehicles found. Try adjusting your filters.</p>
                    {hasFilters && <Button variant="outline" onClick={clearFilters}>Clear Filters</Button>}
                </div>
            ) : (
                <>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
                        {displayed.map(v => (
                            <VehicleCard key={v.vid} vehicle={v} onBuyOnline={handleBuyOnline} />
                        ))}
                    </div>

                    {/* Load more */}
                    {vehicles.length < total && (
                        <div className="flex justify-center pt-4">
                            <Button
                                variant="outline"
                                onClick={() => load(page + 1)}
                                disabled={loading}
                                className="min-w-[160px]"
                            >
                                {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                                Load More ({total - vehicles.length} remaining)
                            </Button>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
